import { FileWithStatus } from '@/types/upload';
import { FileDownloadOutlined } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, Tab, Tabs } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  groupFilesByFolder,
  getEmptyStateMessage,
  getFilesForFilter,
  UploadStatusTabs,
} from '@/components/Upload/utils/uploadUtils';
import { UploadStatusTable } from '@/components/modals/UploadStatusModal/UploadStatusTable';
import { UploadExportPopover } from '@/components/modals/UploadStatusModal/UploadExportPopover';
import { DownloadFormat, createDateFilename, downloadSpreadsheet } from '@/utils/downloadUtils';
import { UploadTestIds } from '@/enums/uploadTestIds.enum';
import { formatFileSize } from '@/utils/format';
import { getFileStatusText } from '@/utils/getFileStatusText';

export const UploadStatusModal = ({
  open,
  onClose,
  completedFiles,
  failedFiles,
  queuedFiles,
  initialFilter = UploadStatusTabs.ALL,
}: {
  open: boolean;
  onClose: () => void;
  completedFiles: FileWithStatus[];
  failedFiles: FileWithStatus[];
  queuedFiles: FileWithStatus[];
  initialFilter?: UploadStatusTabs;
}) => {
  const [filter, setFilter] = useState<UploadStatusTabs>(initialFilter);
  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(new Set());
  const [exportAnchorElement, setExportAnchorElement] = useState<HTMLButtonElement>();
  const [selectedExportOption, setSelectedExportOption] = useState<'current' | 'all'>('current');

  const toggleFolder = (folderPath: string) => {
    setCollapsedFolders(previous => {
      const next = new Set(previous);
      if (next.has(folderPath)) {
        next.delete(folderPath);
      } else {
        next.add(folderPath);
      }
      return next;
    });
  };

  const resetCollapsedFolders = () => {
    setCollapsedFolders(new Set());
  };

  const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setExportAnchorElement(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorElement(undefined);
  };

  const handleExportOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedExportOption(event.target.value as 'current' | 'all');
  };

  const handleExport = (filesToExport: FileWithStatus[], format: DownloadFormat) => {
    const headers = ['Status', 'File Name', 'Path', 'Size', 'Error Message', 'Error code'];
    const rows = filesToExport.map(file => [
      getFileStatusText(file),
      file.name,
      file.path,
      formatFileSize(file.size),
      file.status.message || '',
      file.status.traceId || '',
    ]);

    const data = [headers, ...rows];
    const filename = createDateFilename('upload-status', format);

    downloadSpreadsheet(data, { filename, format });
    handleExportClose();
  };

  const exportPopoverOpen = Boolean(exportAnchorElement);

  useEffect(() => {
    if (open) {
      setFilter(initialFilter);
      resetCollapsedFolders();
    }
  }, [open, initialFilter]);

  const filesForFilter = getFilesForFilter(filter, completedFiles, failedFiles, queuedFiles);
  const folderGroups = groupFilesByFolder(filesForFilter);

  const allFilesCount = completedFiles.length + failedFiles.length + queuedFiles.length;
  const allFiles = [...completedFiles, ...failedFiles, ...queuedFiles];

  const onExport = (format: DownloadFormat) => {
    const filesToExport = selectedExportOption === 'current' ? filesForFilter : allFiles;
    handleExport(filesToExport, format);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      data-testid={UploadTestIds.UploadStatusModal}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            maxWidth: '1020px',
            maxHeight: '70vh',
          },
        },
      }}
    >
      <DialogContent>
        <Tabs
          value={filter}
          onChange={(_, value) => setFilter(value as UploadStatusTabs)}
          aria-label='Upload status filter'
        >
          <Tab
            label={`All (${allFilesCount})`}
            value={UploadStatusTabs.ALL}
            data-testid={UploadTestIds.UploadStatusTabAll}
          />
          <Tab
            label={`Successful (${completedFiles.length})`}
            value={UploadStatusTabs.SUCCESS}
            data-testid={UploadTestIds.UploadStatusTabSuccess}
          />
          <Tab
            label={`Errors (${failedFiles.length})`}
            value={UploadStatusTabs.ERROR}
            data-testid={UploadTestIds.UploadStatusTabError}
          />
          <Tab
            label={`Queued (${queuedFiles.length})`}
            value={UploadStatusTabs.QUEUED}
            data-testid={UploadTestIds.UploadStatusTabQueued}
          />
        </Tabs>
        <UploadStatusTable
          folderGroups={folderGroups}
          collapsedFolders={collapsedFolders}
          toggleFolder={toggleFolder}
          isEmpty={filesForFilter.length === 0}
          emptyStateFilter={filter}
          getEmptyMessage={getEmptyStateMessage}
        />
      </DialogContent>
      <DialogActions>
        <Button
          variant='outlined'
          startIcon={<FileDownloadOutlined />}
          onClick={handleExportClick}
          disabled={allFilesCount === 0}
          data-testid={UploadTestIds.UploadStatusExportButton}
        >
          Export
        </Button>
        <UploadExportPopover
          open={exportPopoverOpen}
          anchorEl={exportAnchorElement}
          onClose={handleExportClose}
          selectedOption={selectedExportOption}
          onOptionChange={handleExportOptionChange}
          onExport={onExport}
          currentTabCount={filesForFilter.length}
          allFilesCount={allFilesCount}
        />
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
